/**
 * Buffers and the Henderson–Hasselbalch equation.
 *
 *     pH = pKa + log₁₀([A⁻] ÷ [HA])
 *
 * It is the equilibrium expression Ka = [H⁺][A⁻] ÷ [HA] rearranged, with the *starting*
 * concentrations of acid and conjugate base standing in for their equilibrium values. That
 * substitution is the approximation. It is good when both concentrations are large beside
 * [H⁺] and [OH⁻], and the equation is usually trusted for ratios between 0.1 and 10, that
 * is, within one pH unit of pKa. `exactBufferPh` solves the same mixture from the charge
 * balance without the substitution, so the difference between the two is the error of the
 * approximation for this particular buffer.
 */

import { fromP, phOfMixture } from "@/lib/tool-kit/chem-reactions/acid-base";

/** pH of a buffer from pKa and the base : acid concentration ratio. */
export function bufferPh(pKa: number, acid: number, base: number): number {
  return pKa + Math.log10(base / acid);
}

/** The [A⁻] ÷ [HA] ratio that gives `pH`. */
export function ratioForPh(pKa: number, pH: number): number {
  return 10 ** (pH - pKa);
}

export interface Shares {
  /** [A⁻] ÷ ([HA] + [A⁻]) */
  base: number;
  /** [HA] ÷ ([HA] + [A⁻]) */
  acid: number;
}

export function shares(ratio: number): Shares {
  return { base: ratio / (1 + ratio), acid: 1 / (1 + ratio) };
}

const RANGE_LOW = 0.1;
const RANGE_HIGH = 10;

/** True when the base : acid ratio lies within the usual 0.1 to 10 buffer range. */
export function inBufferRange(ratio: number): boolean {
  return ratio >= RANGE_LOW * (1 - 1e-12) && ratio <= RANGE_HIGH * (1 + 1e-12);
}

/**
 * pH of the same mixture without the approximation: a weak acid of total concentration
 * [HA] + [A⁻] with [A⁻] of sodium (or similar) ions from its salt.
 */
export function exactBufferPh(k: number, acid: number, base: number): number {
  return phOfMixture({
    solute: { kind: "weak-acid", k },
    concentration: acid + base,
    titrantIon: base,
  });
}

const CLOSE_ENOUGH = 0.05;

/** The approximation is within 0.05 pH units of the exact value. */
export function approximationHolds(
  approximate: number,
  exact: number
): boolean {
  return Math.abs(approximate - exact) <= CLOSE_ENOUGH;
}

export interface Recipe {
  /** mol of the acid form HA */
  acidMoles: number;
  /** mol of the conjugate base A⁻ */
  baseMoles: number;
  ratio: number;
  /** Concentrations in the finished solution, mol/L. */
  acidConcentration: number;
  baseConcentration: number;
}

/** Amounts of HA and A⁻ that make `litres` of buffer at `pH`, with total concentration `total`. */
export function bufferRecipe(
  pKa: number,
  pH: number,
  total: number,
  litres: number
): Recipe {
  const ratio = ratioForPh(pKa, pH);
  const split = shares(ratio);
  const acidConcentration = total * split.acid;
  const baseConcentration = total * split.base;
  return {
    acidMoles: acidConcentration * litres,
    baseMoles: baseConcentration * litres,
    ratio,
    acidConcentration,
    baseConcentration,
  };
}

/** Ka from a typed pKa. */
export function kaFromPka(pKa: number): number {
  return fromP(pKa);
}
